import React, { useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import CircularProgress from '@mui/material/CircularProgress';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Collapse from '@mui/material/Collapse';
import Tooltip from '@mui/material/Tooltip';
import ApprovalExpandablePanel from 'v2/apps/shared/components/approval-expandable-panel';
import ApproverTooltip from 'v2/apps/shared/components/approver-tooltip/approverTooltip';
import { CONSTANTS } from 'clink-components';
import {
  white,
  christmasSilver,
  grayLight,
  clinkRed,
  whiteSmoke,
} from 'v2/constants/colors';
import { connect } from 'react-redux';
import { useContext } from 'hooks/context';
import { useSnackbar } from 'v2/hooks/useSnackbar';
import {
  DRAFT,
  IN_PROGRESS,
  REJECTION_ACKNOWLEDGED,
} from 'v2/helpers/status/approval';
import ApproverModal from 'v2/apps/shared/components/approver-modal/approverModal';
import RejectModal from 'v1/document-creator/components/page/header/RejectModal';
import LogsModal from 'v2/apps/shared/components/logs-modal';
import i18next from 'i18next';
import RejectionAcknowledgeModal from 'v2/apps/clink/pages/projects/RejectionAcknowledgeModal';

const {
  successGreen,
  lightGray,
  darkCharcoal,
  webOrange,
  successDarkGreen,
  mediumGray,
  ghostWhite,
} = CONSTANTS.colors.general;

const getStatusValue = (status) =>
  typeof status === 'string' ? status : status?.label;

const getStatusLabel = (status) => getStatusValue(status) || i18next.t('draft');

const getSubcontractorId = (subcontractor) => subcontractor?.id;

const getStatusColor = (status) => {
  const statusValue = getStatusValue(status);
  const statusLower = statusValue?.toLowerCase() || '';
  const statusColors = {
    pending: { bg: webOrange, color: darkCharcoal },
    approved: { bg: successGreen, color: successDarkGreen },
    rejected: { bg: clinkRed, color: whiteSmoke },
    draft: { bg: lightGray, color: mediumGray },
  };
  return statusColors[statusLower] || statusColors.draft;
};

const isDraft = (status) => {
  if (!status) return true;
  const statusValue = getStatusValue(status);
  return statusValue?.toLowerCase() === 'draft';
};

const isPending = (status) => {
  if (!status) return false;
  const statusValue = getStatusValue(status);
  return statusValue?.toLowerCase() === 'pending';
};

const isRejectionAcknowledged = (status) => {
  if (!status) return false;
  const statusValue = getStatusValue(status);
  return statusValue?.toLowerCase() === 'rejection acknowledged';
};

const isCurrentUserAnApprover = (subcontractor, userId) => {
  if (!userId || !subcontractor) return false;

  const rawApprovals = subcontractor?.approvals;

  const approvalsArray = Array.isArray(rawApprovals)
    ? rawApprovals
    : Object.values(rawApprovals || {}).filter(
        (approval) =>
          (typeof approval?.status === 'string'
            ? approval.status
            : approval?.status?.label
          )?.toLowerCase() === IN_PROGRESS,
      );

  return approvalsArray.some((approval) => {
    if (Array.isArray(approval?.approvers)) {
      return approval.approvers.some((approver) => {
        return (
          String(approver?.approver_user_id) === String(userId) &&
          approver?.status?.label?.toLowerCase() === 'pending'
        );
      });
    }
    return (
      String(approval?.approver_user_id) === String(userId) &&
      approval?.status?.label?.toLowerCase() === 'pending'
    );
  });
};

const isCurrentUserSubmitter = (subcontractor, userId) => {
  if (!userId || !subcontractor) return false;
  return String(subcontractor.submitted_by.id) === String(userId);
};

const isSelectableForApproval = (status) =>
  isDraft(status) || isRejectionAcknowledged(status);

const mapMultiApprovalsToApproverList = (isLevel, approvals = {}) => {
  if (!approvals || typeof approvals !== 'object') return [];

  return Object.values(approvals).map((level) => {
    const approvers = Array.isArray(level?.approvers) ? level.approvers : [];
    return {
      level_number: level?.level ?? null,
      status: level?.status ?? null,
      isLevel,
      rule: level?.rule ?? null,
      approvers: approvers.map((approver) => {
        const user = approver?.user ?? {};
        const status = approver?.status ?? {};

        const firstName = user?.display_name ?? user?.firstName ?? '';
        const lastName = user?.lastname ?? '';

        return {
          id: approver?.id ? String(approver.id) : null,
          role_label: user?.role?.label ?? user?.account_role_label ?? null,
          approver_user: {
            display_name: `${firstName} ${lastName}`.trim(),
            email: user?.email ?? null,
            initials: (
              (firstName?.[0] ?? '') + (lastName?.[0] ?? '')
            ).toUpperCase(),
          },
          is_level_satisfied_by_higher_authority:
            approver?.is_level_satisfied_by_higher_authority ?? null,
          is_level_satisfied_by_self_approved:
            approver?.is_level_satisfied_by_self_approved ?? null,
          is_satisfied_by_higher_authority:
            approver?.is_satisfied_by_higher_authority ?? null,
          is_satisfied_by_self_approved:
            approver?.is_satisfied_by_self_approved ?? null,
          status: {
            value: status?.label?.toLowerCase() ?? null,
            label: status?.label ?? null,
          },
          approval_requested_at: approver?.created_at ?? null,
          actioned_at: approver?.updated_at ?? null,
        };
      }),
    };
  });
};

const mapSingleApprovalsToApproverList = (isLevel, approvals = []) => {
  if (!Array.isArray(approvals)) return [];

  return [
    {
      level_number: null,
      status: null,
      isLevel,
      approvers: approvals.map((approver) => {
        const user = approver?.user ?? {};
        const status = approver?.status ?? {};

        const firstName = user?.display_name ?? user?.firstName ?? '';
        const lastName = user?.lastname ?? '';

        return {
          id: approver?.id ? String(approver.id) : null,

          role_label: user?.role?.label ?? user?.account_role_label ?? null,
          is_level_satisfied_by_higher_authority:
            approver?.is_level_satisfied_by_higher_authority ?? null,
          is_level_satisfied_by_self_approved:
            approver?.is_level_satisfied_by_self_approved ?? null,
          is_satisfied_by_higher_authority:
            approver?.is_satisfied_by_higher_authority ?? null,
          is_satisfied_by_self_approved:
            approver?.is_satisfied_by_self_approved ?? null,

          approver_user: {
            display_name: [firstName, lastName].filter(Boolean).join(' '),
            email: user?.email ?? null,
            initials: (
              (firstName?.[0] ?? '') + (lastName?.[0] ?? '')
            ).toUpperCase(),
          },

          status: {
            value: status?.label?.toLowerCase() ?? null,
            label: status?.label ?? null,
          },

          approval_requested_at: approver?.created_at ?? null,
          actioned_at: approver?.updated_at ?? null,
        };
      }),
    },
  ];
};

const getApproverNames = (approvals, isLevel) => {
  if (!approvals) return [];

  if (isLevel && typeof approvals === 'object' && !Array.isArray(approvals)) {
    return Object.entries(approvals)
      .map(([key, level]) => {
        const approvers = Array.isArray(level?.approvers)
          ? level.approvers
          : [];
        const names = approvers
          .map((approver) => approver?.user?.display_name ?? '')
          .filter(Boolean)
          .join(', ');

        return {
          levelNumber: level.level || key,
          names,
        };
      })
      .filter((item) => item.names);
  }

  if (!isLevel && Array.isArray(approvals)) {
    const names = approvals
      .map((approver) => approver?.user?.display_name ?? '')
      .filter(Boolean)
      .join(', ');

    return names ? [{ levelNumber: null, names }] : [];
  }

  return [];
};

const getRejectionReasons = (approvals, isLevel) => {
  if (!approvals) return '';

  if (isLevel && typeof approvals === 'object' && !Array.isArray(approvals)) {
    const reasons = Object.values(approvals)
      .filter((level) => level?.status === 'rejected')
      .flatMap((level) =>
        (level?.approvers || [])
          .filter((a) => a?.status?.label === 'Rejected' && a?.comment)
          .map((a) => a.comment),
      );

    return reasons.join(', ');
  }

  if (!isLevel && Array.isArray(approvals)) {
    const reasons = approvals
      .filter((a) => a?.status?.label === 'Rejected' && a?.comment)
      .map((a) => a.comment);

    return reasons.join(', ');
  }

  return '';
};

const ShortlistedSubcontractorsTable = ({
  data = [],
  projectId,
  tenderId,
  dispatch,
  clinkAccount,
  bulkUpdateProjectHistory,
}) => {
  const context = useContext('clink');
  const { actions } = context;
  const { showSnackbar } = useSnackbar();
  const [selected, setSelected] = useState([]);
  const [expanded, setExpanded] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [subcontractorListItem, setsubcontractorListItem] = useState(null);
  const [openLogModal, setOpenLogModal] = useState(false);
  const [subcontractorLogs, setSubcontractorLogs] = useState(null);
  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [rejectionSubcontractor, setRejectionSubcontractor] = useState(null);
  const [approvingSubId, setApprovingSubId] = useState(null);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [acknowledgeSubmitting, setAcknowledgeSubmitting] = useState(false);
  const [selectedSubcontractorForAction, setSelectedSubcontractorForAction] =
    useState(null);
  const [rejectionAnchorEl, setRejectionAnchorEl] = useState(null);
  const [expandedRow, setExpandedRow] = React.useState(null);

  const handleExpandClick = (id) => {
    setExpandedRow((prev) => (prev === id ? null : id));
  };

  const fieldsData = {
    title: i18next.t('reject-shortlisted-title'),
    content: i18next.t('reject-shortlisted-description', {
      name:
        selectedSubcontractorForAction?.length === 1
          ? selectedSubcontractorForAction[0]?.name
          : `${selectedSubcontractorForAction?.length || 0} suppliers`,
    }),
    placeholder: i18next.t('reject-shortlisted-placeholder'),
    successMessage: i18next.t('reject-shortlisted-success'),
    errorMessage: i18next.t('reject-shortlisted-error'),
  };

  const accordionSx = {
    boxShadow: 'none',
    border: 'none',
    '&:before': { display: 'none' },
  };

  const accordionSummarySx = {
    padding: 0,
    minHeight: 48,
    '& .MuiAccordionSummary-content': {
      margin: '12px 0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      position: 'static',
      transform: 'none',
      marginLeft: 1,
    },
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(180deg)',
    },
  };

  const requestApprovalButtonSx = {
    backgroundColor: successGreen,
    '&:hover': { backgroundColor: successDarkGreen },
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: '4px',
    mr: 1,
  };

  const selectableItems = data.filter((item) =>
    isSelectableForApproval(item?.status),
  );

  const hasAnyApproverUserId = data.some((item) =>
    isCurrentUserAnApprover(item, clinkAccount?.user?.id),
  );

  const selectableCount = selectableItems.length;
  const selectedCount = selected.length;

  const allSelectedPendingAndCurrentUserIsApprover =
    selectedCount > 0 &&
    selected.every(
      (subcontractor) =>
        isPending(subcontractor?.status) &&
        isCurrentUserAnApprover(subcontractor, clinkAccount?.user?.id),
    );
  const allSelectedDraft =
    selectedCount > 0 &&
    selected.every(
      (sub) => isDraft(sub?.status) || isRejectionAcknowledged(sub?.status),
    );

  const hasMixedDraftAndPending =
    selectedCount > 0 &&
    !allSelectedPendingAndCurrentUserIsApprover &&
    !allSelectedDraft &&
    selected.some((sub) => isDraft(sub?.status) || isPending(sub?.status));

  const handleReasonClick = (event, reason, subcontractor) => {
    event.stopPropagation();
    setPopoverAnchor(event.currentTarget);
    setSelectedReason(reason);
    setRejectionSubcontractor(subcontractor || null);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setSelectedReason('');
    setRejectionSubcontractor(null);
  };

  const handleRejectionPopoverClose = () => {
    setRejectionAnchorEl(null);
    setRejectionSubcontractor(null);
  };

  const handleAcknowledge = useCallback(() => {
    if (!projectId || !tenderId || !rejectionSubcontractor) return;
    const shortlistedId = getSubcontractorId(rejectionSubcontractor);
    if (!shortlistedId) return;

    setAcknowledgeSubmitting(true);
    dispatch(
      actions.acknowledgeRejection({
        projectId,
        tenderId,
        shortlistedSubcontractorId: shortlistedId,
      }),
    )
      .unwrap()
      .then(() => {
        showSnackbar(i18next.t('rejection-acknowledged-success'), 'success');
        handleRejectionPopoverClose();
        return dispatch(
          actions.getProjectProcurement({ pid: projectId }),
        ).unwrap();
      })
      .catch((error) => {
        showSnackbar(
          error?.message || i18next.t('rejection-acknowledged-error'),
          'error',
        );
      })
      .finally(() => {
        setAcknowledgeSubmitting(false);
      });
  }, [
    projectId,
    tenderId,
    rejectionSubcontractor,
    showSnackbar,
    dispatch,
    actions,
  ]);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
    setSelectedSubId(null);
  }, []);

  const triggerBulkUpdateHistoryForApprovedSids = useCallback(
    (sids, subcontractorList) => {
      if (!sids?.length) {
        return false;
      }

      const mappedValues = subcontractorList
        .filter((sub) => sids.includes(sub?.subcontractor_id))
        .map((sub) => ({
          value: sub?.subcontractor_id,
          label: sub?.name,
        }));

      bulkUpdateProjectHistory(
        {
          supplyChain: mappedValues,
        },
        null,
        tenderId,
        true,
      );

      return dispatch(
        actions.getProjectProcurement({ pid: projectId }),
      ).unwrap();
    },
    [actions, dispatch, projectId, tenderId, bulkUpdateProjectHistory],
  );

  const handleApproveOrRejectSubcontractor = useCallback(
    (subcontractors, status, comment = '') => {
      const subcontractorList = Array.isArray(subcontractors)
        ? subcontractors
        : [subcontractors];
      let approvals = [];

      subcontractorList.forEach((subcontractor) => {
        const subId = getSubcontractorId(subcontractor);

        const rawApprovals = subcontractor?.approvals;

        const approvalsArray = Array.isArray(rawApprovals)
          ? rawApprovals
          : Object.values(rawApprovals || {}).filter(
              (approval) =>
                (typeof approval?.status === 'string'
                  ? approval.status
                  : approval?.status?.label
                )?.toLowerCase() === IN_PROGRESS,
            );

        const mapped = approvalsArray
          .flatMap((approval) => {
            if (Array.isArray(approval?.approvers)) {
              return approval.approvers;
            }
            return [approval];
          })
          .filter((approver) => {
            return (
              String(approver?.approver_user_id) ===
              String(clinkAccount?.user?.id)
            );
          })
          .map((approver) => ({
            approval_id: approver?.id,
            shortlisted_subcontractor_id: subId,
          }));

        approvals = [...approvals, ...mapped];
      });

      if (!projectId || !tenderId || approvals.length === 0) return;

      const isBulk = subcontractorList.length > 1;

      if (status?.toLowerCase() === 'approved') {
        if (isBulk) {
          setApprovingSubId('bulk');
        } else {
          setApprovingSubId(getSubcontractorId(subcontractorList[0]));
        }
      }

      if (status?.toLowerCase() === 'rejected') {
        setRejectSubmitting(true);
      }

      dispatch(
        actions.approveOrRejectShortlistedSubcontractor({
          projectId,
          tenderId,
          data: {
            approvals,
            status,
            comment,
          },
        }),
      )
        .unwrap()
        .then((res) => {
          const sids = res?.sids || [];
          const triggered = triggerBulkUpdateHistoryForApprovedSids(
            sids,
            subcontractorList,
          );

          if (status?.toLowerCase() === 'approved' && triggered) {
            showSnackbar(
              i18next.t('subcontractors-added-to-schedule'),
              'success',
            );
          } else {
            showSnackbar(
              i18next.t('supplier-action-success', {
                action: status.toLowerCase(),
              }),
              'success',
            );
          }

          return (
            triggered ||
            dispatch(actions.getProjectProcurement({ pid: projectId })).unwrap()
          );
        })
        .then(() => {
          setRejectModalOpen(false);
          setSelectedSubcontractorForAction(null);
          setSelected([]);
        })
        .catch((error) => {
          showSnackbar(
            error?.message ||
              i18next.t('supplier-action-error', {
                action: `${status.toLowerCase()}ed`,
              }),
            'error',
          );
        })
        .finally(() => {
          if (status?.toLowerCase() === 'approved') {
            setApprovingSubId(null);
          }
          if (status?.toLowerCase() === 'rejected') {
            setRejectSubmitting(false);
          }
        });
    },
    [
      actions,
      dispatch,
      projectId,
      tenderId,
      showSnackbar,
      clinkAccount,
      triggerBulkUpdateHistoryForApprovedSids,
    ],
  );

  const handleSubcontractorWithdrawal = useCallback(
    (subcontractor) => {
      const subId = getSubcontractorId(subcontractor);
      if (!subId || !projectId || !tenderId) return;
      dispatch(
        actions.withdrawShortlistedSubcontractorApproval({
          projectId,
          tenderId,
          shortlistedSubcontractorId: subId,
        }),
      )
        .unwrap()
        .then(() => {
          showSnackbar(i18next.t('supplier-withdraw-success'), 'success');
          if (projectId) {
            return dispatch(
              actions.getProjectProcurement({ pid: projectId }),
            ).unwrap();
          }
          return undefined;
        })
        .then(() => {
          handleMenuClose();
        })
        .catch((error) => {
          showSnackbar(
            error?.message || i18next.t('supplier-withdraw-error'),
            'error',
          );
        });
    },
    [actions, dispatch, projectId, tenderId, showSnackbar, handleMenuClose],
  );

  const handleApproveSubcontractor = useCallback(
    (subcontractor) => {
      handleApproveOrRejectSubcontractor(subcontractor, 'Approved', '');
    },
    [handleApproveOrRejectSubcontractor],
  );

  const handleRejectClick = useCallback((subcontractor) => {
    if (Array.isArray(subcontractor)) {
      setSelectedSubcontractorForAction(subcontractor);
    } else if (subcontractor) {
      setSelectedSubcontractorForAction([subcontractor]);
    }

    setRejectModalOpen(true);
  }, []);

  const handleSelectAllClick = (event) => {
    if (event?.target?.checked) {
      const newSelected = data
        .filter((n) => isSelectableForApproval(n?.status))
        .map((n) => n);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (_, subcontractor) => {
    setSelected((prev) =>
      prev.includes(subcontractor)
        ? prev.filter(
            (prevSubcontractor) => prevSubcontractor !== subcontractor,
          )
        : [...prev, subcontractor],
    );
  };

  const handleRejectSubmit = useCallback(
    (comment) => {
      if (selectedSubcontractorForAction?.length) {
        handleApproveOrRejectSubcontractor(
          selectedSubcontractorForAction,
          'Rejected',
          comment || '',
        );
      }
    },
    [handleApproveOrRejectSubcontractor, selectedSubcontractorForAction],
  );

  const handleMenuOpen = (event, subId, subcontractor) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedSubId(subId);
    setsubcontractorListItem(subcontractor);
  };

  const viewLogs = useCallback(
    (subcontractor) => {
      const subId = getSubcontractorId(subcontractor);

      if (!subId || !projectId || !tenderId) return;

      dispatch(
        actions.getShortlistedSubcontractorLogs({
          projectId,
          tenderId,
          shortlistedSubcontractorId: subId,
        }),
      )
        .unwrap()
        .then((payload) => {
          setSubcontractorLogs(payload);
          setOpenLogModal(true);
        })
        .catch((error) => {
          const errorMessage = error?.message;
          showSnackbar(errorMessage || i18next.t('failed-fetch-logs'), 'error');
        });
    },
    [actions, dispatch, projectId, tenderId, showSnackbar],
  );

  const handleSendShortlistedReminder = useCallback(
    ({ approver_id, shortlistedSubcontractorId }) => {
      if (!projectId || !tenderId)
        return Promise.reject(new Error('Missing project or tender ID'));
      return dispatch(
        actions.sendShortlistedSubcontractorReminder({
          projectId,
          tenderId,
          shortlistedSubcontractorId,
          approverId: approver_id,
        }),
      );
    },
    [actions, dispatch, projectId, tenderId],
  );

  const handleViewLogs = () => {
    if (selectedSubId) {
      const subcontractor =
        subcontractorListItem ||
        data.find((s) => getSubcontractorId(s) === selectedSubId);
      viewLogs(subcontractor);
    }
    handleMenuClose();
  };

  const handleDeleteClick = (sub) => {
    setsubcontractorListItem(sub);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (subcontractorListItem) {
      const shortlistedId = getSubcontractorId(subcontractorListItem);
      if (!shortlistedId || !projectId || !tenderId) return;

      dispatch(
        actions.deleteShortlistedSubcontractor({
          projectId,
          tenderId,
          shortlistedSubcontractorId: shortlistedId,
        }),
      )
        .unwrap()
        .then(() => {
          setDeleteDialogOpen(false);
          setsubcontractorListItem(null);
          setSelected((prev) => prev.filter((id) => id !== shortlistedId));
          showSnackbar(
            i18next.t('delete-shortlisted-success-message'),
            'success',
          );
        })
        .catch(() => {
          showSnackbar(i18next.t('delete-shortlisted-error-message'), 'error');
        });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setsubcontractorListItem(null);
  };

  const handleApprove = () => {
    setModalOpen(true);
  };

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const handleOpen = (event, subcontractor) => {
    event.stopPropagation();
    setRejectionAnchorEl(event.currentTarget);
    setRejectionSubcontractor(subcontractor);
  };

  const selectedSubcontractors = data.filter((subcontractor) => {
    return selected.includes(subcontractor);
  });

  const selectedShortlistedSubcontractorIds = selectedSubcontractors
    .filter((subcontractor) => isSelectableForApproval(subcontractor?.status))
    .map((subcontractor) => subcontractor?.id)
    .filter(Boolean);

  const handleSubmitApproval = async ({ selectedApprovers }) => {
    try {
      if (!projectId || !tenderId) return;

      const response = await dispatch(
        actions.requestShortlistApproval({
          projectId,
          tenderId,
          selectedApprovers,
          selectedShortlistedSubcontractorIds,
        }),
      ).unwrap();

      const sids = response?.sids || [];
      const triggered = triggerBulkUpdateHistoryForApprovedSids(
        sids,
        selectedSubcontractors,
      );
      showSnackbar(
        i18next.t(
          triggered
            ? 'subcontractors-added-to-schedule'
            : 'approval-request-success-message',
        ),
        'success',
      );
      setSelected([]);
      setModalOpen(false);
    } catch (error) {
      showSnackbar(i18next.t('approval-request-error-message'), 'error');
      throw error;
    }
  };

  const renderAccordionSummary = (titleFontSize, disableButton) => (
    <AccordionSummary component="div" sx={accordionSummarySx}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Typography
          variant="p"
          sx={{ fontSize: titleFontSize, fontWeight: 600 }}
        >
          {i18next.t('shortlisted-suppliers')}
        </Typography>
        <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
          <ExpandMoreIcon />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {allSelectedPendingAndCurrentUserIsApprover ||
        hasMixedDraftAndPending ? (
          <>
            <Button
              data-testid="supplier-bulk-approve-btn"
              variant="contained"
              color="primary"
              size="small"
              disabled={hasMixedDraftAndPending}
              onClick={(e) => {
                e.stopPropagation();
                handleApproveOrRejectSubcontractor(selected, 'Approved', '');
              }}
              sx={requestApprovalButtonSx}
            >
              {i18next.t('approve')}
            </Button>
            <Button
              data-testid="supplier-bulk-reject-btn"
              variant="outlined"
              color="secondary"
              size="small"
              disabled={hasMixedDraftAndPending}
              onClick={(e) => {
                e.stopPropagation();
                handleRejectClick(selected);
              }}
              sx={{
                borderRadius: 1,
              }}
            >
              {i18next.t('reject')}
            </Button>
          </>
        ) : (
          <Button
            data-testid="supplier-request-approval-btn"
            variant="contained"
            onClick={(e) => {
              e.stopPropagation();
              handleApprove();
            }}
            disabled={disableButton}
            sx={requestApprovalButtonSx}
          >
            {i18next.t('request-approval')}
          </Button>
        )}
      </Box>
    </AccordionSummary>
  );

  const isSelected = (subId) => selected.includes(subId);
  // Empty state is here!!
  if (!data || data.length === 0) {
    return (
      <Box sx={{ mt: 1, mb: 2 }}>
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          sx={accordionSx}
        >
          {renderAccordionSummary(18, true)}
          <AccordionDetails sx={{ padding: 0 }}>
            <Typography variant="body1" color="text.secondary">
              {i18next.t('no-shortlisted-suppliers-yet')}
            </Typography>
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  }
  return (
    <Box sx={{ mt: 1, mb: 1 }}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={accordionSx}
      >
        {renderAccordionSummary(
          18,
          selectedCount === 0 ||
            (!allSelectedDraft && !allSelectedPendingAndCurrentUserIsApprover),
        )}
        <AccordionDetails sx={{ padding: '16px 0' }}>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: `0px 1px 3px ${grayLight}`,
              border: `1px solid ${christmasSilver}`,
              backgroundColor: ghostWhite,
            }}
          >
            <Table aria-label="shortlisted subcontractors table">
              <TableHead sx={{ backgroundColor: 'white' }}>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="right"
                    >
                      <Checkbox
                        data-testid="supplier-select-all-checkbox"
                        color="primary"
                        indeterminate={
                          selectedCount > 0 && selectedCount < selectableCount
                        }
                        checked={
                          selectableCount > 0 &&
                          selectedCount === selectableCount
                        }
                        onChange={handleSelectAllClick}
                        disabled={selectableCount === 0 || hasAnyApproverUserId}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 14 }}>
                    {i18next.t('supplier-name')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 14 }}>
                    {i18next.t('status')}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: 14 }}>
                    {i18next.t('requested-approver')}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 600,
                      display: 'flex',
                      justifyContent: 'center',
                      fontSize: 14,
                    }}
                  >
                    {i18next.t('actions')}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody sx={{ backgroundColor: 'white' }}>
                {data.map((subcontractor) => {
                  const {
                    name,
                    status,
                    approver_notes: rejectionReason,
                    approvals,
                    isLevel,
                  } = subcontractor;
                  const approverList = isLevel
                    ? mapMultiApprovalsToApproverList(isLevel, approvals)
                    : mapSingleApprovalsToApproverList(isLevel, approvals);

                  const requestedApprover = getApproverNames(
                    approvals,
                    isLevel,
                  );
                  const subId = getSubcontractorId(subcontractor);
                  const isItemSelected = isSelected(subcontractor);
                  const statusLabel = getStatusLabel(status);
                  const colors = getStatusColor(statusLabel);
                  const isApproving = approvingSubId === subId;
                  const isCurrentUserApproverForItem = isCurrentUserAnApprover(
                    subcontractor,
                    clinkAccount?.user?.id,
                  );

                  const isCheckboxDisabled =
                    !isDraft(status) &&
                    !isRejectionAcknowledged(status) &&
                    !(isPending(status) && isCurrentUserApproverForItem);

                  return (
                    <Fragment key={subId}>
                      <TableRow
                        key={subId}
                        hover
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: white,
                          '&.Mui-selected': {
                            backgroundColor: grayLight,
                          },
                          '&.Mui-selected:hover': {
                            backgroundColor: grayLight,
                          },
                          '&:hover': {
                            backgroundColor: grayLight,
                          },
                        }}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="right"
                          >
                            {![DRAFT, REJECTION_ACKNOWLEDGED].includes(
                              statusLabel,
                            ) && (
                              <IconButton
                                data-testid={`supplier-expand-btn-${subId}`}
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleExpandClick(subId);
                                }}
                              >
                                {expandedRow === subId ? (
                                  <KeyboardArrowUpIcon />
                                ) : (
                                  <KeyboardArrowDownIcon />
                                )}
                              </IconButton>
                            )}

                            <Checkbox
                              data-testid={`supplier-checkbox-${subId}`}
                              color="primary"
                              checked={isItemSelected}
                              onChange={(event) =>
                                handleClick(event, subcontractor)
                              }
                              disabled={isCheckboxDisabled}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1.5,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: 14,
                                fontWeight: 600,
                                bgcolor: successGreen,
                                color: white,
                              }}
                            >
                              {name?.charAt(0)?.toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>
                              {name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Chip
                              data-testid={`supplier-status-chip-${subId}`}
                              label={
                                statusLabel === 'Pending'
                                  ? i18next.t('pending-approval')
                                  : statusLabel
                              }
                              size="small"
                              sx={{
                                backgroundColor: colors.bg,
                                color: colors.color,
                                fontWeight: 500,
                                fontSize: 12,
                              }}
                            />
                            {statusLabel?.toLowerCase() === 'rejected' && (
                              <IconButton
                                data-testid={`supplier-rejection-info-btn-${subId}`}
                                sx={{ color: clinkRed }}
                                onClick={(e) => handleOpen(e, subcontractor)}
                              >
                                <InfoOutlinedIcon />
                              </IconButton>
                            )}

                            {isCurrentUserApproverForItem &&
                              statusLabel?.toLowerCase() === 'rejected' &&
                              rejectionReason && (
                                <Typography
                                  component="span"
                                  color="primary"
                                  sx={{
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontWeight: 500,
                                  }}
                                  onClick={(e) =>
                                    handleReasonClick(
                                      e,
                                      rejectionReason,
                                      subcontractor,
                                    )
                                  }
                                >
                                  {i18next.t('reason')}
                                </Typography>
                              )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              requestedApprover.length > 0 ? (
                                <ApproverTooltip
                                  approvers={requestedApprover}
                                />
                              ) : (
                                ''
                              )
                            }
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                maxWidth: '150px',
                              }}
                            >
                              {requestedApprover
                                .map((a) => a.names)
                                .join(', ') || '-'}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'end',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {isCurrentUserApproverForItem &&
                              statusLabel?.toLowerCase() === 'pending' && (
                                <>
                                  <Button
                                    data-testid={`supplier-approve-btn-${subId}`}
                                    variant="contained"
                                    color="primary"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleApproveSubcontractor(subcontractor);
                                    }}
                                    disabled={isApproving}
                                    startIcon={
                                      isApproving ? (
                                        <CircularProgress
                                          size={16}
                                          color="inherit"
                                        />
                                      ) : null
                                    }
                                  >
                                    {i18next.t('approve')}
                                  </Button>
                                  <Button
                                    data-testid={`supplier-reject-btn-${subId}`}
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRejectClick(subcontractor);
                                    }}
                                  >
                                    {i18next.t('reject')}
                                  </Button>
                                </>
                              )}
                            <IconButton
                              data-testid={`supplier-more-actions-btn-${subId}`}
                              aria-label="more actions"
                              onClick={(e) =>
                                handleMenuOpen(e, subId, subcontractor)
                              }
                              size="small"
                            >
                              <MoreVertIcon />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {statusLabel !== 'Draft' && approverList?.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 0, borderBottom: 0 }}>
                            <Collapse
                              in={expandedRow === subId}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Box sx={{ p: 2, backgroundColor: grayLight }}>
                                <ApprovalExpandablePanel
                                  levels={approverList}
                                  sendApprovalReminder={
                                    handleSendShortlistedReminder
                                  }
                                  entity_id={subId}
                                  entityIdKey="shortlistedSubcontractorId"
                                  canSendReminder={
                                    String(subcontractor?.submitted_by?.id) ===
                                    String(clinkAccount?.user?.id)
                                  }
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </AccordionDetails>
      </Accordion>

      <RejectionAcknowledgeModal
        anchorEl={rejectionAnchorEl}
        onClose={handleRejectionPopoverClose}
        width={280}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {rejectionSubcontractor && (
          <Box sx={{ p: 2 }}>
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-word',
                maxHeight: 140,
                overflowY: 'auto',
              }}
            >
              {getRejectionReasons(
                rejectionSubcontractor.approvals,
                rejectionSubcontractor.isLevel,
              )}
            </Typography>

            {isCurrentUserSubmitter(
              rejectionSubcontractor,
              clinkAccount?.user?.id,
            ) && (
              <Box sx={{ display: 'flex', mt: 2 }}>
                <Button
                  data-testid="supplier-acknowledge-btn"
                  size="small"
                  variant="contained"
                  onClick={handleAcknowledge}
                  fullWidth
                  sx={{ borderRadius: 0 }}
                >
                  {i18next.t('acknowledge', { defaultValue: 'Acknowledge' })}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </RejectionAcknowledgeModal>
      <ApproverModal
        id={projectId}
        open={modalOpen}
        onClose={() => handleCloseModal(false)}
        onSubmit={handleSubmitApproval}
        approvalType="supplier_list"
      />
      <RejectModal
        fields={fieldsData}
        type="procurement_schedule"
        open={rejectModalOpen}
        onClose={() => {
          setSelectedSubcontractorForAction([]);
          setRejectModalOpen(false);
        }}
        onReject={handleRejectSubmit}
        projectSlug="procurement_schedule"
        isSubmitting={rejectSubmitting}
      />

      <LogsModal
        open={openLogModal}
        onClose={() => setOpenLogModal(false)}
        allLogs={subcontractorLogs?.logs}
        headerTitle={i18next.t('shortlisted-supplier-logs')}
        entity="Supplier"
        entity_no={subcontractorLogs?.entity_no}
        package_name={subcontractorLogs?.package_name}
      />

      <Popover
        data-testid="supplier-rejection-reason-popover"
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={() => handlePopoverClose()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            {i18next.t('rejection-reason', {
              defaultValue: 'Rejection reason',
            })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {selectedReason || '-'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              data-testid="supplier-popover-acknowledge-btn"
              size="small"
              variant="outlined"
              onClick={handleAcknowledge}
              disabled={acknowledgeSubmitting}
            >
              {i18next.t('acknowledge', { defaultValue: 'Acknowledge' })}
            </Button>
          </Box>
        </Box>
      </Popover>

      <Menu
        data-testid="supplier-actions-menu"
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem data-testid="supplier-menu-view-logs" onClick={handleViewLogs}>{i18next.t('view-logs')}</MenuItem>
        {subcontractorListItem &&
          isPending(subcontractorListItem?.status) &&
          String(subcontractorListItem?.submitted_by?.id) ===
            String(clinkAccount?.user?.id) && (
            <MenuItem
              data-testid="supplier-menu-withdraw"
              onClick={() =>
                handleSubcontractorWithdrawal(subcontractorListItem)
              }
            >
              {i18next.t('withdraw')}
            </MenuItem>
          )}

        {subcontractorListItem &&
          isDraft(subcontractorListItem?.status) &&
          String(subcontractorListItem?.submitted_by?.id) ===
            String(clinkAccount?.user?.id) && (
            <MenuItem data-testid="supplier-menu-delete" onClick={() => handleDeleteClick(subcontractorListItem)}>
              {i18next.t('delete')}
            </MenuItem>
          )}
      </Menu>

      <Dialog
        data-testid="supplier-delete-dialog"
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{i18next.t('delete-supplier')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {i18next.t('delete-shortlisted-confirmation', {
              name: subcontractorListItem?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button data-testid="supplier-delete-cancel-btn" onClick={handleDeleteCancel} sx={{ textTransform: 'none' }}>
            {i18next.t('cancel')}
          </Button>
          <Button
            data-testid="supplier-delete-confirm-btn"
            onClick={handleDeleteConfirm}
            variant="contained"
            sx={{
              backgroundColor: clinkRed,
              '&:hover': {
                backgroundColor: clinkRed,
                opacity: 0.9,
              },
              textTransform: 'none',
            }}
          >
            {i18next.t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ShortlistedSubcontractorsTable.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      status: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  ),
  projectId: PropTypes.number.isRequired,
  tenderId: PropTypes.number.isRequired,
  dispatch: PropTypes.func.isRequired,
  clinkAccount: PropTypes.object,
  bulkUpdateProjectHistory: PropTypes.func,
};

ShortlistedSubcontractorsTable.defaultProps = {
  data: [],
  bulkUpdateProjectHistory: null,
};

const mapStateToProps = (state) => ({
  clinkAccount: state.clinkAccount,
});

export default connect(mapStateToProps)(ShortlistedSubcontractorsTable);
